/**
 * Checkout journey payment/pickup scope boundary (IMP-06, GC-P1-04 / GC-P1-04-tx).
 *
 * Mounted via `withCheckoutScope` on:
 * - `/:tenantCode/post-kiosk` — usually empty pickup `transactionId` until session loads
 * - `/:tenantCode/post-kiosk-failure` — empty scope (no paid session)
 * - `/:tenantCode/checkout` — empty `transactionId` pre-pay; populated after payment start
 * - `/:tenantCode/checkout/handoff`, `/:tenantCode/handoff` — populated when session has tx
 * - `/:tenantCode/checkout/complete` — populated when GET session/status includes transactionId
 *
 * Empty pickup scope: `transactionId: null` whenever the id is unknown (OPEN draft, no
 * payment yet, or APIs omit the field). Populated: non-null `transactionId` once the
 * checkout session (or payment status) DTO carries a known transaction id.
 */
import { useMemo, type ReactNode } from 'react';
import type { PaymentScope } from '../../shared/context/PaymentContext.js';
import { PaymentProvider } from '../../shared/context/PaymentContext.js';
import type { PickupScope } from '../../shared/context/PickupContext.js';
import { PickupProvider } from '../../shared/context/PickupContext.js';
import { useCheckoutSession } from './hooks/useCheckoutSession.js';
import type { CheckoutSessionScopeState } from './hooks/useCheckoutSession.js';

export function derivePaymentScopeFromCheckoutSession(
  state: CheckoutSessionScopeState,
): PaymentScope {
  return {
    checkoutSessionPublicId: state.checkoutSessionPublicId,
    paymentId: state.paymentId,
  };
}

export function derivePickupScopeFromCheckoutSession(
  state: CheckoutSessionScopeState,
): PickupScope {
  if (state.session === undefined && state.transactionId === null) {
    return { transactionId: null, pickupPointId: null };
  }
  return {
    transactionId: state.transactionId,
    pickupPointId: state.session?.collect?.pickupPointId ?? null,
  };
}

export function CheckoutScopeProviders({ children }: { readonly children: ReactNode }): JSX.Element {
  const checkoutSession = useCheckoutSession();

  const paymentScope = useMemo(
    () => derivePaymentScopeFromCheckoutSession(checkoutSession),
    [checkoutSession],
  );

  const pickupScope = useMemo(
    () => derivePickupScopeFromCheckoutSession(checkoutSession),
    [checkoutSession],
  );

  return (
    <PaymentProvider scope={paymentScope}>
      <PickupProvider scope={pickupScope}>{children}</PickupProvider>
    </PaymentProvider>
  );
}
